import { Sport } from '../types/core';

export interface CalculatedStats {
  [key: string]: number;
}

/**
 * Calculate baseball/softball statistics from raw stat entries
 */
export function calculateBaseballStats(rawStats: Array<{ stat_key: string; stat_value: number }>): CalculatedStats {
  const stats: Record<string, number> = {};
  
  // Sum up all stat values by key
  rawStats.forEach(stat => {
    if (!stats[stat.stat_key]) {
      stats[stat.stat_key] = 0;
    }
    stats[stat.stat_key] += parseFloat(stat.stat_value.toString());
  });

  const calculated: CalculatedStats = {};

  // Batting Average (AVG) = H / AB
  if (stats['H'] !== undefined && stats['AB'] !== undefined && stats['AB'] > 0) {
    calculated['AVG'] = stats['H'] / stats['AB'];
  }

  // On-Base Percentage (OBP) = (H + BB + HBP) / (AB + BB + HBP + SF)
  const hits = stats['H'] || 0;
  const walks = stats['BB'] || 0;
  const hitByPitch = stats['HBP'] || 0;
  const sacrificeFly = stats['SF'] || 0;
  const atBats = stats['AB'] || 0;
  
  const numerator = hits + walks + hitByPitch;
  const denominator = atBats + walks + hitByPitch + sacrificeFly;
  
  if (denominator > 0) {
    calculated['OBP'] = numerator / denominator;
  }

  // Slugging Percentage (SLG) = Total Bases / AB
  // Total Bases = 1B + (2B * 2) + (3B * 3) + (HR * 4)
  // If we have individual hit types, use them; otherwise use H as approximation
  const singles = stats['1B'] || 0;
  const doubles = stats['2B'] || 0;
  const triples = stats['3B'] || 0;
  const homeRuns = stats['HR'] || 0;
  
  // If we have individual hit types, calculate total bases accurately
  // Otherwise, estimate: H - (2B + 3B + HR) = singles, then calculate
  let totalBases = 0;
  if (singles > 0 || doubles > 0 || triples > 0 || homeRuns > 0) {
    // We have individual hit types
    totalBases = singles + (doubles * 2) + (triples * 3) + (homeRuns * 4);
  } else if (hits > 0) {
    // Estimate: assume all hits are singles (conservative estimate)
    // This is not ideal, but better than nothing
    totalBases = hits;
  }
  
  if (atBats > 0 && totalBases > 0) {
    calculated['SLG'] = totalBases / atBats;
  }

  // On-Base Plus Slugging (OPS) = OBP + SLG
  if (calculated['OBP'] !== undefined && calculated['SLG'] !== undefined) {
    calculated['OPS'] = calculated['OBP'] + calculated['SLG'];
  }

  // ERA = (Earned Runs * 9) / Innings Pitched.
  // The 9 is the standard normalization: ERA is always "per 9 innings" so pitchers can be
  // compared across leagues (e.g. youth 6/7-inning games vs MLB 9-inning). It is not the
  // number of innings in your game.
  const inningsPitchedForEra = stats['IP'] || 0;
  const earnedRuns = stats['ER'] ?? stats['EarnedRuns'];
  if (stats['ERA'] !== undefined) {
    calculated['ERA'] = stats['ERA'];
  } else if (inningsPitchedForEra > 0 && (earnedRuns !== undefined && earnedRuns > 0)) {
    calculated['ERA'] = (earnedRuns * 9) / inningsPitchedForEra;
  }

  // Walks + Hits per Inning Pitched (WHIP) = (BB_Allowed + H_Allowed) / IP
  const inningsPitched = stats['IP'] || 0;
  const hitsAllowed = stats['H_Allowed'] || 0;
  const walksAllowed = stats['BB_Allowed'] || 0;
  
  if (inningsPitched > 0 && (hitsAllowed > 0 || walksAllowed > 0)) {
    calculated['WHIP'] = (walksAllowed + hitsAllowed) / inningsPitched;
  }

  return calculated;
}

/**
 * Get calculated stats for a sport based on raw stats
 */
export function getCalculatedStats(
  sport: Sport,
  rawStats: Array<{ stat_key: string; stat_value: number }>
): CalculatedStats {
  if (sport === 'Baseball' || sport === 'Softball') {
    return calculateBaseballStats(rawStats);
  }
  
  // Add other sport calculations here as needed
  return {};
}

/**
 * Get display label for a calculated stat
 * Returns the abbreviation (key) itself since we're using abbreviations
 */
export function getCalculatedStatLabel(statKey: string): string {
  // Just return the key as the label (we're using abbreviations)
  return statKey;
}

/**
 * Format a calculated stat value for display
 */
export function formatCalculatedStat(value: number, statKey: string): string {
  // Stats that should be displayed as percentages (0.000 format)
  const percentageStats = ['AVG', 'OBP', 'SLG', 'OPS'];
  
  if (percentageStats.includes(statKey)) {
    return value.toFixed(3);
  }
  
  // Stats that should be displayed with 2 decimal places
  const decimalStats = ['ERA', 'WHIP'];
  if (decimalStats.includes(statKey)) {
    return value.toFixed(2);
  }
  
  // Default: show as integer if whole number, otherwise 2 decimals
  return value % 1 === 0 ? value.toString() : value.toFixed(2);
}

